import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import ProgramCard from "@/components/ProgramCard";
import { api } from "@/lib/api";
import { useSubscription } from "@/hooks/useSubscription";
import { Search, X, ShieldCheck, Zap } from "lucide-react";

const FORMATS = [
  { value: "curso", label: "Curso" },
  { value: "diplomado", label: "Diplomado" },
  { value: "programa", label: "Programa" },
  { value: "seminario", label: "Seminario" },
  { value: "certificacion", label: "Certificación" },
];
const MODALITIES = [
  { value: "online", label: "Online" },
  { value: "blended", label: "Blended" },
  { value: "presencial", label: "Presencial" },
  { value: "mixto", label: "Mixto" },
];

export default function Catalogo() {
  const [areas, setAreas] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [selArea, setSelArea] = useState([]);
  const [selFormat, setSelFormat] = useState([]);
  const [selModality, setSelModality] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hasAllAccess } = useSubscription();
  const limit = 24;

  useEffect(() => { api.get("/catalog/areas").then((r) => setAreas(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    selArea.forEach((a) => params.append("area", a));
    selFormat.forEach((f) => params.append("format", f));
    selModality.forEach((m) => params.append("modality", m));
    params.append("page", page);
    params.append("limit", limit);
    api.get(`/catalog/programs?${params.toString()}`)
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [q, selArea, selFormat, selModality, page]);

  useEffect(() => { setPage(1); }, [q, selArea, selFormat, selModality]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const toggle = (arr, setArr, value) => {
    setArr(arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value]);
  };

  const activeFiltersCount = selArea.length + selFormat.length + selModality.length + (q ? 1 : 0);

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Catálogo</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B0B0B] font-display">193 programas del sector financiero y bursátil</h1>
            <p className="mt-2 text-gray-600 max-w-2xl">
              Filtra por área, formato o modalidad. Da clic en cualquier programa para ver el temario resumido y solicitar información.
            </p>
          </div>
          <div className="font-mono text-sm text-gray-600" data-testid="catalog-total-count">
            <span className="text-[#0B0B0B] font-semibold">{total}</span> programas
          </div>
        </div>

        {hasAllAccess ? (
          <div
            className="mb-6 p-4 rounded-xl border border-primary/40 bg-primary/5 flex items-start gap-3"
            data-testid="all-access-active-banner"
          >
            <ShieldCheck className="w-5 h-5 text-[#B84A0C] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#B84A0C]">
                Tu All-Access está activo
              </p>
              <p className="text-xs text-[#B84A0C]/80">
                Todos los programas publicados están incluidos sin cargo adicional.
              </p>
            </div>
            <Link
              to="/app/alumno/suscripcion"
              className="text-xs font-semibold text-[#B84A0C] hover:text-primary underline underline-offset-2"
            >
              Gestionar →
            </Link>
          </div>
        ) : (
          <Link
            to="/planes"
            data-testid="catalog-all-access-promo"
            className="mb-6 p-4 rounded-xl border border-dashed border-gray-300 bg-gradient-to-r from-[#0B0F14] via-[#111827] to-[#0B0F14] text-white flex items-center justify-between gap-3 hover:border-[#F26A21] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F26A21]/20 border border-[#F26A21]/40 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#F26A21]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">
                  ¿Vas a tomar varios programas? All-Access desde $2,500 MXN/mes
                </p>
                <p className="text-xs text-gray-400">
                  Acceso ilimitado a los 195+ programas mientras estés suscrito. Cancela cuando quieras.
                </p>
              </div>
            </div>
            <span className="hidden sm:inline text-xs font-bold text-[#F26A21] uppercase tracking-widest">
              Ver planes →
            </span>
          </Link>
        )}

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filters */}
          <aside className="lg:col-span-1 space-y-6" data-testid="catalog-filters">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">Buscar</label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
                <input
                  data-testid="catalog-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ej. Riesgo, Basel, M&A"
                  className="w-full bg-[#F9FAFB] border border-gray-200 rounded-md pl-9 pr-8 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]"
                />
                {q && (
                  <button className="absolute right-2 top-2 text-gray-500 hover:text-[#0B0B0B]" onClick={() => setQ("")} aria-label="clear">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <FilterGroup title="Área" testid="filter-area" items={areas.map((a) => ({ value: a.slug, label: a.name }))} selected={selArea} onToggle={(v) => toggle(selArea, setSelArea, v)} />
            <FilterGroup title="Formato" testid="filter-format" items={FORMATS} selected={selFormat} onToggle={(v) => toggle(selFormat, setSelFormat, v)} />
            <FilterGroup title="Modalidad" testid="filter-modality" items={MODALITIES} selected={selModality} onToggle={(v) => toggle(selModality, setSelModality, v)} />

            {activeFiltersCount > 0 && (
              <button
                onClick={() => { setSelArea([]); setSelFormat([]); setSelModality([]); setQ(""); }}
                data-testid="clear-filters-btn"
                className="text-xs text-[#F26A21] hover:text-[#0B0B0B] underline underline-offset-2"
              >
                Limpiar filtros ({activeFiltersCount})
              </button>
            )}
          </aside>

          {/* Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-48 bg-[#F9FAFB] border border-gray-200 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-gray-200 rounded-xl">
                <p className="text-gray-600">No hay programas que coincidan con tu búsqueda.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="catalog-grid">
                {items.map((p) => <ProgramCard key={p.id} program={p} hasAllAccess={hasAllAccess} />)}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2" data-testid="catalog-pagination">
                <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 text-sm border border-gray-300 rounded text-[#0B0B0B] disabled:opacity-40 hover:bg-black/5">
                  Anterior
                </button>
                <span className="font-mono text-sm text-gray-600">
                  <span className="text-[#0B0B0B]">{page}</span> / {totalPages}
                </span>
                <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 text-sm border border-gray-300 rounded text-[#0B0B0B] disabled:opacity-40 hover:bg-black/5">
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

function FilterGroup({ title, items, selected, onToggle, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-600 mb-2">{title}</p>
      <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
        {items.map((it) => {
          const active = selected.includes(it.value);
          return (
            <button
              key={it.value}
              onClick={() => onToggle(it.value)}
              data-testid={`${testid}-option-${it.value}`}
              className={`w-full text-left text-sm px-3 py-1.5 rounded transition-colors ${
                active ? "bg-[#F26A21]/30 text-[#0B0B0B] border border-[#F26A21]/70" : "text-[#0B0B0B] hover:bg-black/5 border border-transparent"
              }`}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
